import { useState } from "react";
import api, { setToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Lock, Eye, EyeOff } from "lucide-react";

const LOGO = "https://customer-assets.emergentagent.com/job_service-tracker-265/artifacts/k1nm2r2h_D_NQ_NP_880992-MLA83949983996_042025-O.webp";

const errorText = (detail) => {
  if (!detail) return "No se pudo ingresar. Intente nuevamente.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return detail.msg || String(detail);
};

export default function Login({ onSuccess }) {
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { password });
      setToken(data.token);
      onSuccess();
    } catch (err) {
      setError(err.response ? errorText(err.response.data?.detail) : "Sin conexión con el servidor");
      setPassword("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-6">
      <div data-testid="login-screen" className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-8">
          <img src={LOGO} alt="Logo" className="w-20 h-20 object-contain mb-4 rounded-xl bg-white p-1" />
          <h1 className="text-3xl font-bold tracking-tight text-white">Sistema de Órdenes</h1>
          <p className="text-sm text-zinc-400 mt-1">Acceso exclusivo del taller</p>
        </div>
        <form onSubmit={handleSubmit} className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 space-y-4 shadow-2xl">
          <label htmlFor="access_password" className="block text-xs text-zinc-400 uppercase tracking-wider">
            Clave de acceso
          </label>
          <div className="relative">
            <Lock className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <Input
              id="access_password"
              data-testid="login-password-input"
              type={show ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
              autoComplete="current-password"
              className="h-12 pl-9 pr-10 bg-zinc-950 border-zinc-700 text-white font-mono text-lg focus-visible:ring-orange-500"
            />
            <button
              type="button"
              data-testid="login-toggle-password"
              onClick={() => setShow((s) => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
              aria-label="Mostrar clave"
            >
              {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {error && (
            <p data-testid="login-error" className="text-sm text-red-400 bg-red-950/40 border border-red-900 rounded-md px-3 py-2">
              {error}
            </p>
          )}
          <Button
            type="submit"
            data-testid="login-submit-btn"
            disabled={loading || !password}
            className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white text-base font-semibold transition-all active:scale-[0.98]"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Ingresar"}
          </Button>
        </form>
        <p className="text-center text-[11px] text-zinc-600 mt-6">La clave se pide cada vez que se abre la aplicación.</p>
      </div>
    </div>
  );
}
